import { useEffect, useState } from "react";

import TiltAndSpinVisualization, {
  TiltAndSpinVisualizationProps,
} from "@/assets/TiltAndSpinVisualization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

type TiltAndSpinChartCardProps = {
  title?: string;
  // 'auto' means legend is visible if the visualization has more than one dataset
  showLegend?: true | false | "auto";
  children?: React.ReactNode;
} & React.ComponentProps<typeof Card> &
  TiltAndSpinVisualizationProps;

export const TiltAndSpinChartCard = ({
  title = "Tilt and Spin",
  datasets = [],
  showLegend = "auto",
  minSpin = 1000,
  maxSpin = 3000,
  rings,
  ...cardProps
}: TiltAndSpinChartCardProps) => {
  const [isLegendVisible, setLegendVisible] = useState(false);

  useEffect(() => {
    setLegendVisible(
      showLegend === true || (showLegend === "auto" && datasets.length > 1),
    );
  }, [showLegend, datasets.length]);

  return (
    <Card className="w-full max-w-lg p-1 flex flex-col" {...cardProps}>
      <CardHeader className="p-2">
        <CardTitle className="text-xs md:text-sm text-center">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="overflow-hidden flex-1">
        <Separator className="mb-2" />
        <div className="w-full h-full relative">
          <ChartContainer
            config={{}}
            className="aspect-square max-h-[350px] w-full"
          >
            <TiltAndSpinVisualization
              rings={rings}
              datasets={datasets}
              minSpin={minSpin}
              maxSpin={maxSpin}
            />
          </ChartContainer>
          {isLegendVisible && (
            <div className="absolute right-0 top-0">
              {datasets.map(({ name, color }) => (
                <div className={`flex items-center space-x-2`} key={name}>
                  <span
                    className={cn(`inline-block w-2 h-2 rounded-full`)}
                    style={{ backgroundColor: color }}
                    aria-hidden="true"
                    data-testid={`legend-for-${name}`}
                  />
                  <h3 className="text-xs">{name}</h3>
                </div>
              ))}
            </div>
          )}
          {rings && rings.length > 0 && (
            <div className="absolute bottom-0 left-0 text-xs">
              RPM Rings: {rings.join(", ")}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export { TiltAndSpinChartDataCard } from "./TiltAndSpinChartDataCard";
